import type {
  HarnessClinic,
  HarnessClinicLeadForm,
  StartHarnessRunBody,
} from '@borradh-workspace/api-client/types';
import { ClipboardList, Send } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

const NO_FORM = '__none__';
const NO_AD = '__none__';

/** Used when the clinic has no form of its own: what every Meta form asks. */
const BASIC_QUESTIONS: HarnessClinicLeadForm['questions'] = [
  { name: 'full_name', label: 'Full name', type: 'FULL_NAME', options: [] },
  { name: 'email', label: 'Email', type: 'EMAIL', options: [] },
  { name: 'phone_number', label: 'Phone', type: 'PHONE', options: [] },
];

type LeadFormBody = Extract<StartHarnessRunBody, { kind: 'lead_form' }>;

/**
 * A customer submits one of the clinic's real Meta lead forms. The answers
 * are stored exactly as the lead-form webhook stores them, and Claire sends
 * the opener production would send — then the conversation continues as the
 * lead.
 */
export function LeadFormPanel({
  clinic,
  customerName,
  disabled,
  busy,
  onSubmit,
}: {
  clinic: HarnessClinic;
  customerName: string;
  disabled: boolean;
  busy: boolean;
  onSubmit: (body: LeadFormBody, formName: string | null) => void;
}) {
  const forms = clinic.leadForms ?? [];
  const [formId, setFormId] = useState<string>(forms[0]?.id ?? NO_FORM);
  const form = forms.find((item) => item.id === formId) ?? null;
  const questions = form?.questions.length ? form.questions : BASIC_QUESTIONS;
  const [values, setValues] = useState<Record<string, string>>({});
  const [channel, setChannel] = useState<'whatsapp' | 'sms'>(
    clinic.channels.includes('whatsapp') ? 'whatsapp' : 'sms'
  );
  // Suggest an ad that opens this form; any other ad can still be picked.
  const linked = useMemo(
    () =>
      clinic.ads.filter((ad) => form?.adMetaIds.includes(ad.metaAdId) ?? false),
    [clinic.ads, form]
  );
  const [adMetaId, setAdMetaId] = useState<string>(NO_AD);

  // A new clinic or form starts from its own answers.
  useEffect(() => {
    setFormId(forms[0]?.id ?? NO_FORM);
  }, [forms]);
  useEffect(() => {
    setAdMetaId(linked[0]?.metaAdId ?? NO_AD);
    setValues({});
  }, [linked]);

  const answerFor = (name: string, type: string) =>
    values[name] ??
    (type === 'FULL_NAME'
      ? customerName
      : type === 'FIRST_NAME'
        ? (customerName.split(' ')[0] ?? '')
        : '');

  const submit = () => {
    const fields = questions
      .map((question) => ({
        name: question.name,
        value: answerFor(question.name, question.type).trim(),
      }))
      .filter((field) => field.value);
    onSubmit(
      {
        kind: 'lead_form',
        organizationId: clinic.id,
        channel,
        customerName: customerName.trim() || 'Test Customer',
        adMetaId: adMetaId === NO_AD ? null : adMetaId,
        formId: form?.id ?? null,
        fields,
      },
      form?.name ?? null
    );
  };

  return (
    <form
      className="w-full max-w-xl space-y-4 rounded-lg border bg-background p-4 text-left"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="flex items-center gap-2">
        <ClipboardList className="size-4 text-muted-foreground" />
        <p className="font-medium text-sm">A customer submits a lead form</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field className="sm:col-span-2">
          <FieldLabel htmlFor="lead-form-form">Form</FieldLabel>
          <Select value={formId} onValueChange={setFormId}>
            <SelectTrigger id="lead-form-form" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {forms.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                  {item.adMetaIds.length
                    ? ` · ${item.adMetaIds.length} ad${item.adMetaIds.length === 1 ? '' : 's'}`
                    : ''}
                </SelectItem>
              ))}
              <SelectItem value={NO_FORM}>
                {forms.length
                  ? 'No form: name, email and phone only'
                  : 'This clinic has no lead forms: name, email and phone'}
              </SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel htmlFor="lead-form-ad">From the ad</FieldLabel>
          <Select value={adMetaId} onValueChange={setAdMetaId}>
            <SelectTrigger id="lead-form-ad" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_AD}>No ad</SelectItem>
              {[
                ...linked,
                ...clinic.ads.filter((ad) => !linked.includes(ad)),
              ].map((ad) => (
                <SelectItem key={ad.metaAdId} value={ad.metaAdId}>
                  {linked.includes(ad) ? '★ ' : ''}
                  {ad.headline || ad.name || ad.metaAdId}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field>
          <FieldLabel>Claire replies on</FieldLabel>
          <ToggleGroup
            type="single"
            variant="outline"
            value={channel}
            onValueChange={(value) =>
              value && setChannel(value as 'whatsapp' | 'sms')
            }
          >
            <ToggleGroupItem value="whatsapp">WhatsApp</ToggleGroupItem>
            <ToggleGroupItem value="sms">SMS</ToggleGroupItem>
          </ToggleGroup>
        </Field>
      </div>

      <div className="space-y-3 border-t pt-3">
        {questions.map((question) => {
          const id = `lead-form-q-${question.name}`;
          const value = answerFor(question.name, question.type);
          const set = (next: string) =>
            setValues((current) => ({ ...current, [question.name]: next }));
          return (
            <Field key={question.name}>
              <FieldLabel htmlFor={id}>{question.label}</FieldLabel>
              {question.options.length ? (
                <Select value={value} onValueChange={set}>
                  <SelectTrigger id={id} className="w-full">
                    <SelectValue placeholder="Pick an answer" />
                  </SelectTrigger>
                  <SelectContent>
                    {question.options.map((option) => (
                      <SelectItem key={option.key} value={option.key}>
                        {option.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={id}
                  value={value}
                  onChange={(event) => set(event.target.value)}
                  type={question.type === 'EMAIL' ? 'email' : 'text'}
                  placeholder={
                    question.type === 'PHONE'
                      ? 'Leave empty for a safe test number'
                      : undefined
                  }
                />
              )}
            </Field>
          );
        })}
      </div>

      <Button type="submit" disabled={disabled || busy} className="w-full">
        <Send />
        Submit the form
      </Button>
      <p className="text-muted-foreground text-xs">
        Makes the lead on the Harness database and runs the opener production
        sends. Nothing reaches anyone.
      </p>
    </form>
  );
}
